// src/hooks/usePermission.js
import { useContext } from "react";
import { PermissionMenuContext } from "../contextApi/permissionBasedMenuContext";
import { useCurrentUser } from "./useCurrentUser";

/**
 * Custom hook to check capabilities (permissions) on the frontend.
 * 
 * Usage options:
 * 1. Single check (returns a single boolean in an array for easy destructuring):
 *    const [canCreate] = usePermission("quantative-data.create");
 * 
 * 2. Multiple checks (returns an array of booleans):
 *    const [canCreate, canAssign] = usePermission(["quantative-data.create", "data-owner.assign"]);
 * 
 * 3. Helper functions & raw arrays (when called with no arguments):
 *    const { hasPermission, userCapabilities } = usePermission();
 * 
 * @param {string|string[]} [capabilitiesToCheck] Single capability string or array of capabilities.
 * @returns {boolean[] | {hasPermission: function, userCapabilities: string[]}}
 */
export function usePermission(capabilitiesToCheck) {
  const context = useContext(PermissionMenuContext);
  const { currentUserId, currentUser } = useCurrentUser();

  if (!context) {
    throw new Error("usePermission must be used within a PermissionBasedMenuProvider");
  }

  const { userCapabilities = [] } = context;

  const isAdmin = userCapabilities.includes("*") || 
                  (currentUserId && Number(currentUserId) === 1) || 
                  (currentUser && currentUser.parent_id === null);

  const checkSingle = (permissionOrCapability) => {
    if (!permissionOrCapability) return false;

    // 1. Wildcard super-user bypass
    if (isAdmin) return true;

    // 2. Direct capability check
    return userCapabilities.includes(permissionOrCapability);
  };

  // If no arguments passed, return capabilities and helper
  if (capabilitiesToCheck === undefined) {
    return {
      hasPermission: checkSingle,
      userCapabilities,
    };
  }

  // If array is passed, check each and return array of booleans
  if (Array.isArray(capabilitiesToCheck)) {
    return capabilitiesToCheck.map(checkSingle);
  }

  // If single string is passed, return [boolean] to support: const [canCreate] = usePermission("...")
  return [checkSingle(capabilitiesToCheck)];
}
